/**
 * Bug Report Button
 *
 * Floating bottom-right button + modal for submitting a bug report.
 * Captures recent console logs + system info (via consoleCapture) and an
 * optional html2canvas screenshot, then submits through the tRPC API
 * (trpc.bugs.submit). The browser never touches the DB directly.
 */

import { useState, useEffect, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { trpc } from '../lib/trpc';
import { useAuthStore } from '../stores/authStore';
import { useCampaignStore } from '../stores/campaignStore';
import {
  getCapturedLogs,
  getSystemInfo,
} from '../lib/consoleCapture';
import {
  BUG_REPORT_TITLE_MAX,
  BUG_REPORT_DESCRIPTION_MAX,
  BUG_REPORT_SCREENSHOT_MAX,
} from '@machina/core';
import { Icon } from './Icon';
import { isAdmin } from './admin/AdminPanel';

const GOLD = '#d4af37';

export function BugReportButton() {
  const { user } = useAuthStore();
  const currentCampaignId = useCampaignStore((s) => s.currentCampaignId);

  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [logCount, setLogCount] = useState(0);
  const [openCount, setOpenCount] = useState<number | null>(null);

  const admin = isAdmin(user?.email);

  // Admin badge: poll the open-report count.
  const loadOpenCount = useCallback(async () => {
    if (!admin) return;
    try {
      const n = await trpc.bugs.openCount.query();
      setOpenCount(n);
    } catch {
      // ignore — badge is best-effort
    }
  }, [admin]);

  useEffect(() => {
    loadOpenCount();
  }, [loadOpenCount]);

  // Refresh log count whenever the modal opens.
  useEffect(() => {
    if (open) setLogCount(getCapturedLogs().length);
  }, [open]);

  function resetForm() {
    setTitle('');
    setDescription('');
    setScreenshot(null);
    setError(null);
    setSuccess(false);
  }

  function handleClose() {
    setOpen(false);
    resetForm();
  }

  async function handleCapture() {
    setCapturing(true);
    setError(null);
    try {
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        allowTaint: true,
        scale: Math.min(0.5, window.devicePixelRatio || 1),
        ignoreElements: (el) =>
          el.classList?.contains('bug-report-modal') ||
          el.classList?.contains('bug-report-button'),
      });

      let quality = 0.7;
      let dataUrl = canvas.toDataURL('image/jpeg', quality);
      while (dataUrl.length > BUG_REPORT_SCREENSHOT_MAX && quality > 0.3) {
        quality -= 0.1;
        dataUrl = canvas.toDataURL('image/jpeg', quality);
      }

      if (dataUrl.length > BUG_REPORT_SCREENSHOT_MAX) {
        setError('Screenshot too large to attach — submitting without it.');
        setScreenshot(null);
      } else {
        setScreenshot(dataUrl);
      }
    } catch {
      setError('Screenshot capture failed.');
    } finally {
      setCapturing(false);
    }
  }

  async function handleSubmit() {
    if (!user) {
      setError('You must be logged in to submit a bug report.');
      return;
    }
    if (!title.trim()) {
      setError('A title is required.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await trpc.bugs.submit.mutate({
        title: title.trim(),
        description: description.trim() || undefined,
        consoleLogs: getCapturedLogs(),
        screenshotDataUrl: screenshot ?? undefined,
        systemInfo: getSystemInfo(),
        currentRoute: window.location.pathname + window.location.search,
        browserInfo: navigator.userAgent,
        screenSize: `${window.innerWidth}x${window.innerHeight}`,
        campaignId: currentCampaignId ?? undefined,
      });

      setSuccess(true);
      loadOpenCount();
      setTimeout(handleClose, 2000);
    } catch (err) {
      setError((err as Error).message || 'Failed to submit bug report.');
    } finally {
      setSubmitting(false);
    }
  }

  if (!user) return null;

  return (
    <>
      {/* Floating button */}
      <button
        className="bug-report-button"
        onClick={() => setOpen(true)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        title="Report a bug"
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          width: 48,
          height: 48,
          borderRadius: '50%',
          backgroundColor: '#14101a',
          border: `2px solid ${hovered ? GOLD : '#3a3242'}`,
          color: hovered ? GOLD : '#c0c0c0',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(0,0,0,0.5)',
          transition: 'all 0.15s ease',
          zIndex: 9998,
        }}
      >
        <Icon name="bug" size={22} />
        {admin && openCount !== null && openCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              padding: '0 4px',
              borderRadius: 9,
              backgroundColor: GOLD,
              color: '#0a080c',
              fontSize: 11,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #0a080c',
            }}
          >
            {openCount}
          </span>
        )}
      </button>

      {/* Modal */}
      {open && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 16,
          }}
          onClick={handleClose}
        >
          <div
            className="bug-report-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#14101a',
              border: `1px solid ${GOLD}`,
              borderRadius: 8,
              width: '100%',
              maxWidth: 480,
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              fontFamily: 'Georgia, var(--font-display, serif)',
              color: '#e0dcd0',
            }}
          >
            {/* Header */}
            <div style={s.header}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon name="bug" size={18} color={GOLD} />
                Report a Bug
              </span>
              <button onClick={handleClose} style={s.iconBtn} title="Close">
                <Icon name="close" size={18} />
              </button>
            </div>

            {/* Body */}
            <div style={s.body}>
              {success ? (
                <div style={s.successBox}>
                  <Icon name="check" size={32} color="#2ecc71" />
                  <span>Thanks! Your bug report was submitted.</span>
                </div>
              ) : (
                <>
                  {error && (
                    <div style={s.error}>
                      <Icon name="alert" size={14} />
                      {error}
                    </div>
                  )}

                  <label style={s.label}>
                    <span style={s.labelRow}>
                      <span>Title</span>
                      <span style={s.counter}>
                        {title.length}/{BUG_REPORT_TITLE_MAX}
                      </span>
                    </span>
                    <input
                      type="text"
                      value={title}
                      maxLength={BUG_REPORT_TITLE_MAX}
                      placeholder="Short summary of the bug"
                      onChange={(e) => setTitle(e.target.value)}
                      style={s.input}
                    />
                  </label>

                  <label style={s.label}>
                    <span style={s.labelRow}>
                      <span>Description</span>
                      <span style={s.counter}>
                        {description.length}/{BUG_REPORT_DESCRIPTION_MAX}
                      </span>
                    </span>
                    <textarea
                      value={description}
                      maxLength={BUG_REPORT_DESCRIPTION_MAX}
                      placeholder="What happened? What did you expect?"
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      style={{ ...s.input, resize: 'vertical', minHeight: 80 }}
                    />
                  </label>

                  {/* Screenshot */}
                  <div>
                    <button
                      onClick={handleCapture}
                      disabled={capturing}
                      style={s.secondaryBtn}
                    >
                      <Icon name={capturing ? 'loader' : 'upload'} size={14} />
                      {capturing
                        ? 'Capturing...'
                        : screenshot
                          ? 'Recapture screenshot'
                          : 'Capture screenshot'}
                    </button>
                    {screenshot && (
                      <div style={{ marginTop: 8 }}>
                        <img
                          src={screenshot}
                          alt="Screenshot preview"
                          style={{
                            width: '100%',
                            borderRadius: 4,
                            border: '1px solid #3a3242',
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Attachment info */}
                  <div style={s.infoBox}>
                    <div>
                      <Icon name="scroll" size={12} color={GOLD} />{' '}
                      {logCount} console log{logCount !== 1 ? 's' : ''} will be
                      attached
                    </div>
                    <div style={{ color: '#8a8294', fontSize: 11, marginTop: 4 }}>
                      {window.location.pathname} •{' '}
                      {`${window.innerWidth}x${window.innerHeight}`}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            {!success && (
              <div style={s.footer}>
                <button onClick={handleClose} style={s.secondaryBtn}>
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !title.trim()}
                  style={{
                    ...s.primaryBtn,
                    opacity: submitting || !title.trim() ? 0.5 : 1,
                    cursor: submitting || !title.trim() ? 'default' : 'pointer',
                  }}
                >
                  {submitting ? <Icon name="loader" size={14} /> : <Icon name="bug" size={14} />}
                  {submitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid #2a2430',
    color: GOLD,
    fontWeight: 600,
    fontSize: 16,
  },
  iconBtn: {
    background: 'transparent',
    border: 'none',
    color: '#8a8294',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  body: {
    padding: 16,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 13,
  },
  labelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: '#c0c0c0',
  },
  counter: {
    fontSize: 11,
    color: '#8a8294',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 10px',
    backgroundColor: '#0a080c',
    border: '1px solid #3a3242',
    borderRadius: 4,
    color: '#e0dcd0',
    fontSize: 14,
    fontFamily: 'inherit',
  },
  infoBox: {
    padding: '10px 12px',
    backgroundColor: '#1a1520',
    border: '1px solid #2a2430',
    borderRadius: 4,
    fontSize: 12,
    color: '#c0c0c0',
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 10px',
    backgroundColor: 'rgba(220,53,69,0.12)',
    border: '1px solid #c0392b',
    borderRadius: 4,
    color: '#e57373',
    fontSize: 12,
  },
  successBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    padding: '24px 12px',
    textAlign: 'center',
    color: '#c0c0c0',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    padding: '12px 16px',
    borderTop: '1px solid #2a2430',
  },
  primaryBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    backgroundColor: GOLD,
    border: 'none',
    borderRadius: 4,
    color: '#0a080c',
    fontWeight: 600,
    fontSize: 14,
    fontFamily: 'inherit',
  },
  secondaryBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    backgroundColor: 'transparent',
    border: `1px solid ${GOLD}`,
    borderRadius: 4,
    color: GOLD,
    fontSize: 13,
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
};
